import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_STUDY_YEAR_PERIODS } from '@/api/endpoints';
import { STUDY_YEAR_PERIODS_QUERY_KEY } from '@/api/queryKeys';
import { useStudyYearStart } from '@/pages/Overview/hooks/useStudyYearStart';
import { getFallbackPeriods } from '@/pages/Overview/period';
import { StudyYearPeriodType, type StudyYearPeriod } from '@/types/overview';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';

interface StudyYearPeriodResponse {
  name: string;
  datePeriod?: { from?: string; to?: string };
  typeCode?: string;
}

const KNOWN_PERIOD_TYPES: ReadonlySet<string> = new Set(
  Object.values(StudyYearPeriodType),
);

const sortIndexByType: Record<StudyYearPeriodType, number> = {
  [StudyYearPeriodType.Semester]: 0,
  [StudyYearPeriodType.Year]: 1,
  [StudyYearPeriodType.Month]: 2,
  [StudyYearPeriodType.Unknown]: 2,
  [StudyYearPeriodType.Custom]: 3,
};

const toStudyYearPeriod = ({
  name,
  datePeriod,
  typeCode,
}: StudyYearPeriodResponse): StudyYearPeriod | undefined => {
  const { from, to } = datePeriod ?? {};

  if (!from || !to) {
    return undefined;
  }

  const type =
    typeCode && KNOWN_PERIOD_TYPES.has(typeCode)
      ? (typeCode as StudyYearPeriodType)
      : StudyYearPeriodType.Unknown;

  if (type === StudyYearPeriodType.Month) {
    return undefined;
  }

  return { name, from: new Date(from), to: new Date(to), type };
};

const isPeriod = (
  period: StudyYearPeriod | undefined,
): period is StudyYearPeriod => !!period;

const byTypeThenName = (
  period: StudyYearPeriod,
  otherPeriod: StudyYearPeriod,
): number =>
  sortIndexByType[period.type] - sortIndexByType[otherPeriod.type] ||
  period.name.localeCompare(otherPeriod.name);

const fetchStudyYearPeriods = async (): Promise<StudyYearPeriod[]> => {
  try {
    const periods = await apiClient
      .get<StudyYearPeriodResponse[]>(API_STUDY_YEAR_PERIODS)
      .then(unwrapAxiosResponse);

    return periods.map(toStudyYearPeriod).filter(isPeriod).sort(byTypeThenName);
  } catch (error) {
    logRequestError(ErrorMessage.StudyYearPeriods, error);

    return [];
  }
};

interface UseStudyYearPeriodsResult {
  periods: StudyYearPeriod[];
  arePeriodsLoading: boolean;
}

export const useStudyYearPeriods = (): UseStudyYearPeriodsResult => {
  const studyYearStart = useStudyYearStart();
  const { data, isLoading } = useQuery<StudyYearPeriod[]>({
    queryKey: [...STUDY_YEAR_PERIODS_QUERY_KEY, studyYearStart],
    queryFn: fetchStudyYearPeriods,
    staleTime: Infinity,
  });

  return {
    periods: data?.length ? data : getFallbackPeriods(studyYearStart),
    arePeriodsLoading: isLoading,
  };
};
